const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const headers = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

// credentials:'include' so the Cloudflare Access session cookie rides along on cross-subdomain
// (app.→api.) requests once Access is enforced. Harmless before enforcement.
const get = async (path, params = {}) => {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));
  const qs = new URLSearchParams(clean).toString();
  const res = await fetch(`${API_URL}/api/crm${path}${qs ? '?' + qs : ''}`, { headers: headers(), credentials: 'include' });
  if (!res.ok) throw new Error((await res.json()).error || res.statusText);
  return res.json();
};

const post = async (path, body) => {
  const res = await fetch(`${API_URL}/api/crm${path}`, {
    method: 'POST', headers: headers(), credentials: 'include', body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error((await res.json()).error || res.statusText);
  return res.json();
};

const put = async (path, body) => {
  const res = await fetch(`${API_URL}/api/crm${path}`, {
    method: 'PUT', headers: headers(), credentials: 'include', body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error((await res.json()).error || res.statusText);
  return res.json();
};

const del = async (path) => {
  const res = await fetch(`${API_URL}/api/crm${path}`, { method: 'DELETE', headers: headers(), credentials: 'include' });
  if (!res.ok) throw new Error((await res.json()).error || res.statusText);
  return res.json();
};

export const crmService = {
  getDashboard: () => get('/dashboard'),

  // Network
  getNetwork: (params) => get('/network', params),
  getMarket: (params) => get('/network/market', params),

  // Subsidiaries
  getSubsidiaries: () => get('/subsidiaries'),
  createSubsidiary: (data) => post('/subsidiaries', data),
  updateSubsidiary: (id, data) => put(`/subsidiaries/${id}`, data),

  // Product lines
  getProductLines: () => get('/product-lines'),
  createProductLine: (data) => post('/product-lines', data),
  updateProductLine: (id, data) => put(`/product-lines/${id}`, data),

  // Organizations
  getLandscape: () => get('/landscape'),
  getOrganizations: (params) => get('/organizations', params),
  getOrganization: (id) => get(`/organizations/${id}`),
  createOrganization: (data) => post('/organizations', data),
  updateOrganization: (id, data) => put(`/organizations/${id}`, data),
  deleteOrganization: (id) => del(`/organizations/${id}`),

  // Contacts
  getContacts: (params) => get('/contacts', params),
  bulkCreateContacts: (payload) => post('/contacts/bulk', payload),
  getContact: (id) => get(`/contacts/${id}`),
  createContact: (data) => post('/contacts', data),
  updateContact: (id, data) => put(`/contacts/${id}`, data),
  deleteContact: (id) => del(`/contacts/${id}`),
  assignContactToSubsidiary: (id, data) => post(`/contacts/${id}/subsidiaries`, data),

  // Relationships
  createRelationship: (data) => post('/relationships', data),
  deleteRelationship: (id) => del(`/relationships/${id}`),

  // Opportunities
  getOpportunities: (params) => get('/opportunities', params),
  getOpportunity: (id) => get(`/opportunities/${id}`),
  createOpportunity: (data) => post('/opportunities', data),
  updateOpportunity: (id, data) => put(`/opportunities/${id}`, data),
  addOpportunityContact: (id, data) => post(`/opportunities/${id}/contacts`, data),
  removeOpportunityContact: (id, contactId) => del(`/opportunities/${id}/contacts/${contactId}`),
  getOpportunityValueEstimate: (id) => get(`/opportunities/${id}/value-estimate`),

  // Interactions
  getInteractions: (params) => get('/interactions', params),
  createInteraction: (data) => post('/interactions', data),
  updateInteraction: (id, data) => put(`/interactions/${id}`, data),
  deleteInteraction: (id) => del(`/interactions/${id}`),

  // Competitors
  getCompetitors: (params) => get('/competitors', params),
  createCompetitor: (data) => post('/competitors', data),
  updateCompetitor: (id, data) => put(`/competitors/${id}`, data),

  // Verticals
  getVerticals: () => get('/verticals'),
  getVerticalCoverage: (id) => get(`/verticals/${id}/coverage`),
  getVerticalProjection: () => get('/verticals/projection'),

  // Tusks — tech development & commercialization
  searchPSC: (q) => get('/psc', { q }),
  getTechProjects: () => get('/tech-projects'),
  createTechProject: (data) => post('/tech-projects', data),
  updateTechProject: (id, data) => put(`/tech-projects/${id}`, data),
  reclassifyTechProject: (id) => post(`/tech-projects/${id}/reclassify`, {}),
  reclassifyAllTechProjects: () => post('/tech-projects/reclassify-all', {}),
  getTechProjectOpportunityMatches: (id) => get(`/tech-projects/${id}/opportunity-matches`),
  linkTechProjectOpportunity: (id, oppId) => post(`/tech-projects/${id}/opportunities`, { opportunity_id: oppId }),
  unlinkTechProjectOpportunity: (id, oppId) => del(`/tech-projects/${id}/opportunities/${oppId}`),
  addTechNeed: (projectId, data) => post(`/tech-projects/${projectId}/needs`, data),
  updateTechNeed: (id, data) => put(`/tech-needs/${id}`, data),
  getOpenTechNeeds: () => get('/tech-needs/open'),
  promoteTechNeed: (id) => post(`/tech-needs/${id}/promote`, {}),
  getSupplyChainRiskSummary: () => get('/supply-chain/risk-summary'),
  postTechProgressUpdate: (projectId, text) => post(`/tech-projects/${projectId}/updates`, { update_text: text }),

  // The Family — internal team roster
  getTeam: () => get('/team'),
  createTeamMember: (data) => post('/team', data),
  joinTeam: () => post('/team/self', {}),
  updateTeamMember: (id, data) => put(`/team/${id}`, data),
  assignTeamMember: (id, data) => post(`/team/${id}/assignments`, data),
  unassignTeamMember: (id, projectId) => del(`/team/${id}/assignments/${projectId}`),
  getFutureState: () => get('/team/future-state'),
  getMyEfforts: () => get('/team/my-efforts'),
  bulkCreateTeam: (payload) => post('/team/bulk', payload),

  // Onboarding & Lead Navigator
  inviteTeamMember: (id) => post(`/team/${id}/invite`, {}),
  requestTeamMember: (data) => post('/team/requests', data),
  getTeamRequests: () => get('/team/requests'),
  resolveTeamRequest: (id, data) => put(`/team/requests/${id}`, data),
  submitNavigatorFeedback: (body) => post('/navigator', { body }),
  postNavigatorAnnouncement: (body) => post('/navigator/announce', { body }),
  getNavigatorFeed: () => get('/navigator'),
  getNavigatorPending: () => get('/navigator/pending'),
  moderateNavigator: (id, data) => put(`/navigator/${id}`, data),
  addSubsidiaryVertical: (subsidiaryId, verticalId) => post(`/subsidiaries/${subsidiaryId}/verticals`, { vertical_id: verticalId }),
  removeSubsidiaryVertical: (subsidiaryId, verticalId) => del(`/subsidiaries/${subsidiaryId}/verticals/${verticalId}`),
  addOpportunityVertical: (opportunityId, verticalId) => post(`/opportunities/${opportunityId}/verticals`, { vertical_id: verticalId }),
  removeOpportunityVertical: (opportunityId, verticalId) => del(`/opportunities/${opportunityId}/verticals/${verticalId}`),

  // Growth pipeline
  getGrowthActions: (params) => get('/growth', params),
  getGrowthSummary: () => get('/growth/summary'),
  createGrowthAction: (data) => post('/growth', data),
  updateGrowthAction: (id, data) => put(`/growth/${id}`, data),
  deleteGrowthAction: (id) => del(`/growth/${id}`),
  addGrowthVertical: (id, verticalId) => post(`/growth/${id}/verticals`, { vertical_id: verticalId }),
  removeGrowthVertical: (id, vid) => del(`/growth/${id}/verticals/${vid}`),

  // Supply chain / Sankey
  getSankey: () => get('/supply-chain/sankey'),
  getSupplyChainComponents: () => get('/supply-chain/components'),
  createSupplyChainComponent: (data) => post('/supply-chain/components', data),
  updateSupplyChainComponent: (id, data) => put(`/supply-chain/components/${id}`, data),
  deleteSupplyChainComponent: (id) => del(`/supply-chain/components/${id}`),
  addComponentVertical: (id, verticalId) => post(`/supply-chain/components/${id}/verticals`, { vertical_id: verticalId }),
  removeComponentVertical: (id, vid) => del(`/supply-chain/components/${id}/verticals/${vid}`),

  // Military equipment
  getMilitaryEquipment: () => get('/military-equipment'),
  createMilitaryEquipment: (data) => post('/military-equipment', data),
  updateMilitaryEquipment: (id, data) => put(`/military-equipment/${id}`, data),
  linkEquipmentComponent: (equipId, compId, criticality) =>
    post(`/military-equipment/${equipId}/components`, { component_id: compId, criticality }),
  unlinkEquipmentComponent: (equipId, compId) => del(`/military-equipment/${equipId}/components/${compId}`),

  // Military capabilities
  getMilitaryCapabilities: () => get('/military-capabilities'),
  createMilitaryCapability: (data) => post('/military-capabilities', data),
  updateMilitaryCapability: (id, data) => put(`/military-capabilities/${id}`, data),
  linkCapabilityEquipment: (capId, equipId) => post(`/military-capabilities/${capId}/equipment`, { equipment_id: equipId }),
  unlinkCapabilityEquipment: (capId, equipId) => del(`/military-capabilities/${capId}/equipment/${equipId}`),

  // Knowledge base
  getKnowledgeTerms: (params) => get('/knowledge', params),
  createKnowledgeTerm: (data) => post('/knowledge', data),
  updateKnowledgeTerm: (id, data) => put(`/knowledge/${id}`, data),
  deleteKnowledgeTerm: (id) => del(`/knowledge/${id}`),

  // Funding watch
  getFundingWatch: (params) => get('/funding-watch', params),
  createFundingWatch: (data) => post('/funding-watch', data),
  updateFundingWatch: (id, data) => put(`/funding-watch/${id}`, data),
  deleteFundingWatch: (id) => del(`/funding-watch/${id}`),
  addFundingWatchVertical: (id, verticalId) => post(`/funding-watch/${id}/verticals`, { vertical_id: verticalId }),
  removeFundingWatchVertical: (id, vid) => del(`/funding-watch/${id}/verticals/${vid}`),
  promoteFundingWatch: (id) => post(`/funding-watch/${id}/promote`, {}),
  syncFundingWatchNow: () => post('/funding-watch/sync-now', {}),
  syncSECEDGAR: () => post('/admin/sync-sec-edgar', {}),
  addManualCompetitor: (data) => post('/competitors/manual', data),
  ingestCompetitorProducts: (id) => post(`/competitors/${id}/ingest`, {}),
  getCompetitorIngestStatus: (id) => get(`/competitors/${id}/ingest/status`),

  // Government opportunities & awards
  getGovOpportunities: (params) => get('/gov-opportunities', params),
  getGovAwards: (params) => get('/gov-awards', params),
  getFollowOnOpportunities: (parentId) => get(`/opportunities/${parentId}/follow-ons`),
  getAwardDigest: () => get('/award-digest/latest'),
  syncGovOpportunities: () => post('/admin/sync-gov-opportunities', {}),
  syncGovAwards: () => post('/admin/sync-gov-awards', {}),
  createGovOpportunity: (data) => post('/opportunities/manual', data),
  estimateGovOpportunity: (params) => get('/opportunities/estimate-gov', params),
  addFollowOnOpportunity: (parentId, data) => post(`/opportunities/${parentId}/follow-on`, data),

  // Users (HQ only)
  getUsers: () => get('/users'),
  setUserRole: (id, role) => put(`/users/${id}/role`, { role }),

  // Outlook
  getOutlookStatus: () => get('/outlook/status'),
  getOutlookAuthUrl: () => get('/outlook/auth-url'),
  syncOutlook: () => post('/outlook/sync', {}),

  // ── BD Planning Calendar ──────────────────────────────────────────────────
  // Exposure is derived server-side by services/fiscal-exposure.js, never sent
  // up from the client — the board and the briefing read the same rule.
  getCalendarBoard: (params) => get('/calendar/board', params),
  getCalendarTriage: () => get('/calendar/triage'),
  getCalendarAppropriations: () => get('/calendar/appropriations'),
  getCalendarAuthorities: () => get('/calendar/authorities'),
  getFiscalMarkers: (params) => get('/calendar/fiscal-markers', params),
  getExposureLegend: () => get('/calendar/legend'),

  // Engagements — our dated work, tied to opportunities and contacts
  getEngagements: (params) => get('/engagements', params),
  getEngagement: (id) => get(`/engagements/${id}`),
  createEngagement: (data) => post('/engagements', data),
  updateEngagement: (id, data) => put(`/engagements/${id}`, data),
  deleteEngagement: (id) => del(`/engagements/${id}`),
  addEngagementContact: (id, data) => post(`/engagements/${id}/contacts`, data),
  removeEngagementContact: (id, contactId) => del(`/engagements/${id}/contacts/${contactId}`),
  logEngagementInteraction: (id, data) => post(`/engagements/${id}/log-interaction`, data),

  // Four-stream board: pipeline, tusk, gov_opportunity, event
  pinToCalendar: (data) => post('/calendar/pin', data),

  // TRL ladder for a Tusk
  getTechMilestones: (params) => get('/tech-milestones', params),
  createTechMilestone: (data) => post('/tech-milestones', data),
  updateTechMilestone: (id, data) => put(`/tech-milestones/${id}`, data),
  deleteTechMilestone: (id) => del(`/tech-milestones/${id}`),

  // Event catalog — conferences, industry days, symposia
  getEvents: (params) => get('/events', params),
  getEvent: (id) => get(`/events/${id}`),
  createEvent: (data) => post('/events', data),
  updateEvent: (id, data) => put(`/events/${id}`, data),
  deleteEvent: (id) => del(`/events/${id}`),
  verifyEvent: (id) => post(`/events/${id}/verify`, {}),
  setEventAttendance: (id, data) => post(`/events/${id}/attendance`, data),
  addEventTarget: (id, data) => post(`/events/${id}/targets`, data),
  updateEventTarget: (targetId, data) => put(`/events/targets/${targetId}`, data),
  deleteEventTarget: (targetId) => del(`/events/targets/${targetId}`),
  promoteEvent: (id, data) => post(`/events/${id}/promote`, data),
  syncSamEvents: (data) => post('/events/sync-sam', data || {}),
  ingestEventSite: (data) => post('/events/ingest', data),
  getEventSourceStatus: () => get('/events/ingest/status'),
  getEventReviewQueue: () => get('/events/review-queue'),

  // Organization link verification
  getUnverifiedOrgLinks: (params) => get('/unverified-org-links', params),
  verifyOrgLink: (data) => post('/verify-org-link', data),
  rejectOrgLink: (data) => post('/reject-org-link', data),
};
